import { useEffect, useRef, useState } from 'react';

export const useUnsavedChanges = (formMethods, initialData = {}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const initialDataRef = useRef(initialData);
  const pendingActionRef = useRef(null);

  const { watch, getValues } = formMethods;

  // Function to deeply compare objects while handling special cases
  const compareData = (current, initial) => {
    // Handle null/undefined cases
    if (!current && !initial) return false;
    if (!current || !initial) return true;

    // Convert to strings for deep comparison, handling special cases
    const currentStr = JSON.stringify(current, (key, value) => {
      // Ignore empty password fields
      if (key === 'password' && (!value || value === '')) return undefined;
      // Handle File objects
      if (value instanceof File) return value.name;
      // Handle dates
      if (value instanceof Date) return value.toISOString();
      // Handle arrays - sort for consistent comparison
      if (Array.isArray(value)) return [...value].sort();
      return value;
    });
    
    const initialStr = JSON.stringify(initial, (key, value) => {
      if (key === 'password' && (!value || value === '')) return undefined;
      if (value instanceof File) return value.name;
      if (value instanceof Date) return value.toISOString();
      if (Array.isArray(value)) return [...value].sort();
      return value;
    });

    return currentStr !== initialStr;
  };

  // Watch for form changes
  useEffect(() => {
    const subscription = watch((data) => {
      const hasChanges = compareData(data, initialDataRef.current);
      setHasUnsavedChanges(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  // Update initial data when form is successfully saved
  const updateInitialData = (newData) => {
    initialDataRef.current = newData;
    setHasUnsavedChanges(false);
  };

  // Handle navigation/close attempts
  const handleUnsavedChanges = (action) => {
    if (hasUnsavedChanges) {
      pendingActionRef.current = action;
      setShowUnsavedModal(true);
      return false; // Prevent the action
    }
    return true; // Allow the action
  };

  // Modal actions
  const handleSaveChanges = async (onSave) => {
    try {
      const formData = getValues();
      await onSave(formData);
      setHasUnsavedChanges(false);
      setShowUnsavedModal(false);
      
      // Execute pending action
      if (pendingActionRef.current) {
        pendingActionRef.current();
        pendingActionRef.current = null;
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      // Keep modal open on error
    }
  };

  const handleDiscardChanges = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    
    // Execute pending action
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
  };

  const handleCancelAction = () => {
    setShowUnsavedModal(false);
    pendingActionRef.current = null;
  };

  // Listen for page close attempts
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return {
    hasUnsavedChanges,
    showUnsavedModal,
    handleUnsavedChanges,
    handleSaveChanges,
    handleDiscardChanges,
    handleCancelAction,
    updateInitialData
  };
};